/**
 * Facebook Accounts page. Lists the current user's linked Business Manager
 * accounts and lets them:
 *  - link a new account (BM id + System User id + Access Token),
 *  - sync ad accounts from Facebook for a given linked account,
 *  - remove a linked account (cascades server-side).
 * The access token is write-only: it is never returned by the API.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import Card from '@/components/Cards/Card';
import Table, { type Column } from '@/components/Tables/Table';
import Button from '@/components/Forms/Button';
import Input from '@/components/Forms/Input';
import Modal from '@/components/Modals/Modal';
import Badge from '@/components/common/Badge';
import Alert from '@/components/Alerts/Alert';
import { useToast } from '@/components/Alerts/toastContext';
import * as facebookAccountService from '@/services/facebookAccountService';
import * as adAccountService from '@/services/adAccountService';
import type { CreateFacebookAccountInput, FacebookAccountDTO } from '@shared/types';
import { formatDate, formatDateTime } from '@/utils/formatters';
import { required } from '@/utils/validators';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Derive a token-expiry badge from `tokenExpiresAt`. */
function tokenBadge(expiresAt: string | null): ReactElement {
  if (!expiresAt) return <Badge color="gray">Không rõ</Badge>;
  const t = new Date(expiresAt).getTime();
  if (Number.isNaN(t)) return <Badge color="gray">Không rõ</Badge>;
  const diff = t - Date.now();
  if (diff <= 0) return <Badge color="red">Đã hết hạn</Badge>;
  if (diff < 7 * DAY_MS) return <Badge color="yellow">Sắp hết hạn</Badge>;
  return <Badge color="green">Còn hiệu lực</Badge>;
}

/** Facebook accounts management view. */
export default function FacebookAccounts(): ReactElement {
  const toast = useToast();

  const [accounts, setAccounts] = useState<FacebookAccountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create-account modal state.
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<CreateFacebookAccountInput>({
    businessManagerId: '',
    systemUserId: '',
    accessToken: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof CreateFacebookAccountInput, string>>>({});

  // Per-row action state.
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FacebookAccountDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  // "Connect with Facebook" (OAuth) state.
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await facebookAccountService.list();
      setAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải danh sách tài khoản Facebook');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Kick off the Facebook Login-for-Business OAuth dialog. */
  const handleConnectFacebook = useCallback(async (): Promise<void> => {
    setConnecting(true);
    try {
      const authUrl = await facebookAccountService.getAuthUrl();
      window.location.href = authUrl;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Không thể bắt đầu đăng nhập Facebook');
      setConnecting(false);
    }
  }, [toast]);

  // Handle the redirect back from the Facebook OAuth callback (status query).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fb_connected')) {
      toast.success('Đã kết nối tài khoản Facebook thành công');
      window.history.replaceState({}, '', window.location.pathname);
      void load();
    } else if (params.get('fb_error')) {
      toast.error(`Kết nối Facebook thất bại (${params.get('fb_error')})`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = (): void => {
    setForm({ businessManagerId: '', systemUserId: '', accessToken: '' });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof CreateFacebookAccountInput, string>> = {};
    const bm = required(form.businessManagerId);
    if (bm) errors.businessManagerId = bm;
    const su = required(form.systemUserId);
    if (su) errors.systemUserId = su;
    const at = required(form.accessToken);
    if (at) errors.accessToken = at;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const created = await facebookAccountService.create({
        businessManagerId: form.businessManagerId.trim(),
        systemUserId: form.systemUserId.trim(),
        accessToken: form.accessToken.trim(),
      });
      setAccounts((prev) => [created, ...prev]);
      toast.success('Đã liên kết tài khoản Facebook');
      setCreateOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Liên kết tài khoản thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSync = async (account: FacebookAccountDTO): Promise<void> => {
    setSyncingId(account.id);
    try {
      const result = await adAccountService.sync(account.id);
      toast.success(result.message ?? `Đã đồng bộ ${result.synced} tài khoản quảng cáo`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đồng bộ tài khoản quảng cáo thất bại');
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await facebookAccountService.remove(deleteTarget.id);
      setAccounts((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      toast.success('Đã xóa liên kết tài khoản Facebook');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Xóa tài khoản thất bại');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<FacebookAccountDTO>[] = [
    {
      key: 'name',
      header: 'Tên doanh nghiệp',
      render: (row) =>
        row.name ? (
          <span className="font-medium text-gray-900">{row.name}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    { key: 'businessManagerId', header: 'Business Manager ID' },
    { key: 'systemUserId', header: 'System User ID' },
    {
      key: 'token',
      header: 'Trạng thái Token',
      render: (row) => tokenBadge(row.tokenExpiresAt),
    },
    {
      key: 'tokenExpiresAt',
      header: 'Hết hạn',
      render: (row) => (row.tokenExpiresAt ? formatDateTime(row.tokenExpiresAt) : '—'),
    },
    {
      key: 'createdAt',
      header: 'Ngày tạo',
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'actions',
      header: 'Hành động',
      align: 'right',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            loading={syncingId === row.id}
            onClick={() => void handleSync(row)}
          >
            Đồng bộ Ad Accounts
          </Button>
          <Button variant="danger" onClick={() => setDeleteTarget(row)}>
            Xóa
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tài khoản BM</h1>
          <p className="mt-1 text-sm text-gray-500">
            Quản lý các tài khoản Business Manager đã liên kết.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button loading={connecting} onClick={() => void handleConnectFacebook()}>
            Kết nối bằng Facebook
          </Button>
          <Button variant="secondary" onClick={() => setCreateOpen(true)}>
            Liên kết thủ công
          </Button>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <Card>
        <Table
          columns={columns}
          rows={accounts}
          loading={loading}
          rowKey={(row) => row.id}
          emptyMessage="Chưa có tài khoản Facebook nào. Hãy liên kết tài khoản đầu tiên."
        />
      </Card>

      {/* Create modal */}
      <Modal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          resetForm();
        }}
        title="Liên kết tài khoản Facebook"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreateOpen(false);
                resetForm();
              }}
            >
              Hủy
            </Button>
            <Button type="submit" form="fb-account-form" loading={submitting}>
              Liên kết
            </Button>
          </>
        }
      >
        <form id="fb-account-form" onSubmit={handleCreate} noValidate className="space-y-4">
          <Input
            label="Business Manager ID"
            value={form.businessManagerId}
            onChange={(e) => setForm((f) => ({ ...f, businessManagerId: e.target.value }))}
            error={formErrors.businessManagerId}
            placeholder="Ví dụ: 1234567890"
            required
          />
          <Input
            label="System User ID"
            value={form.systemUserId}
            onChange={(e) => setForm((f) => ({ ...f, systemUserId: e.target.value }))}
            error={formErrors.systemUserId}
            placeholder="Ví dụ: 9876543210"
            required
          />
          <Input
            label="Access Token"
            type="password"
            autoComplete="off"
            value={form.accessToken}
            onChange={(e) => setForm((f) => ({ ...f, accessToken: e.target.value }))}
            error={formErrors.accessToken}
            placeholder="Dán System User Access Token"
            required
          />
          <p className="text-xs text-gray-500">
            Access Token được mã hóa và lưu trữ an toàn, không bao giờ hiển thị lại.
          </p>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Xác nhận xóa"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Hủy
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void handleDelete()}>
              Xóa
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Bạn có chắc muốn xóa liên kết tài khoản{' '}
          <span className="font-semibold">{deleteTarget?.businessManagerId}</span>? Toàn bộ tài
          khoản quảng cáo liên quan cũng sẽ bị xóa.
        </p>
      </Modal>
    </div>
  );
}
